import { call, put, select, takeLatest, all } from "redux-saga/effects";
import {
  notificationsRequest,
  notificationsSuccess,
  notificationsFailure,
  markReadRequest,
  markReadSuccess,
  markReadFailure,
  markSeenRequest,
  markSeenSuccess,
  markSeenFailure,
  markAllSeenRequest,
  markAllSeenSuccess,
  markAllSeenFailure,
} from "./notificationsActions";
import {
  getNotifications,
  deleteNotificationRead,
  patchNotificationSeen,
} from "../../services/api";
import { RootState } from "..";

// small mapper if you need to normalize fields
function mapItem(d: any) {
  return {
    id: String(d.id),
    title: String(d.template.title ?? ""),
    body: d.template.message ?? "",
    createdAt: String(d.createdAt ?? d.created_at ?? new Date().toISOString()),
    read: Boolean(d.isSeen ?? d.isSeen ?? false),
    level: d.value,
    type: d.template.type,
    valueParts: d.value,
    url: d.url,
  };
}

function* fetchNotificationsWorker() {
  try {
    const playerId: string | undefined = yield select(
      (s: RootState) => s.auth.user?.id,
    );

    if (!playerId) {
      yield put(notificationsFailure("Missing player id"));
      return;
    }

    const raw = yield call(getNotifications, playerId, {
      _start: 0,
      _end: 50,
    });
    // If API returns { data: [...] }
    console.log(raw);
    const list = Array.isArray(raw?.items)
      ? raw.items
      : Array.isArray(raw.items)
        ? raw.items
        : [];
    const items = list.map(mapItem);
    console.log(items);

    yield put(notificationsSuccess({ items }));
  } catch (e: any) {
    yield put(
      notificationsFailure(e?.message || "Failed to load notifications"),
    );
  }
}

function* markReadWorker(action: ReturnType<typeof markReadRequest>) {
  try {
    const { notificationId } = action.payload;
    yield call(deleteNotificationRead, notificationId);
    yield put(markReadSuccess({ notificationId }));
    yield put(notificationsRequest());
  } catch (e: any) {
    yield put(markReadFailure(e?.message || "Failed to mark as read"));
  }
}

function* markSeenWorker(action: ReturnType<typeof markSeenRequest>) {
  try {
    const { notificationId } = action.payload;
    const playerId: string | undefined = yield select(
      (s: RootState) => s.auth.user?.id,
    );

    if (!playerId) {
      yield put(markSeenFailure("Missing player id"));
      return;
    }

    yield call(patchNotificationSeen, notificationId, playerId);
    yield put(markSeenSuccess({ notificationId }));
  } catch (e: any) {
    yield put(markSeenFailure(e?.message || "Failed to mark as seen"));
  }
}

function* markAllSeenWorker() {
  try {
    const playerId: string | undefined = yield select(
      (s: RootState) => s.auth.user?.id,
    );

    if (!playerId) {
      yield put(markAllSeenFailure("Missing player id"));
      return;
    }

    const notifications: any[] = yield select(
      (s: RootState) => s.notifications.items,
    );

    // Get all unread notifications
    const unreadNotifications = notifications.filter((n) => !n.read);

    if (unreadNotifications.length === 0) {
      yield put(markAllSeenSuccess());
      return;
    }

    // Mark all unread notifications as seen in parallel
    yield all(
      unreadNotifications.map((notification) =>
        call(patchNotificationSeen, notification.id, playerId, true),
      ),
    );

    // Update all notifications in state
    yield all(
      unreadNotifications.map((notification) =>
        put(markSeenSuccess({ notificationId: notification.id })),
      ),
    );

    yield put(markAllSeenSuccess());
  } catch (e: any) {
    yield put(markAllSeenFailure(e?.message || "Failed to mark all as seen"));
  }
}

export function* notificationsRootSaga() {
  yield takeLatest(notificationsRequest.type, fetchNotificationsWorker);
  yield takeLatest(markReadRequest.type, markReadWorker);
  yield takeLatest(markSeenRequest.type, markSeenWorker);
  yield takeLatest(markAllSeenRequest.type, markAllSeenWorker);
}
